// Dashboard.jsx — Executive Climate Risk Overview
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Paper, Button, Chip, CircularProgress, Table, TableHead, TableRow,
  TableCell, TableBody, Tooltip,
} from "@mui/material";
import { Database, Building2, Banknote, AlertTriangle, RefreshCw, ArrowRight, TrendingUp } from "lucide-react";
import { toast } from "react-toastify";
import DashboardLayout from "../layouts/DashboardLayout";
import KpiCard from "../components/KpiCard";
import RiskPill from "../components/RiskPill";
import ScenarioToggle from "../components/ScenarioToggle";
import { dashboardApi, riskApi } from "../api/api";
import {
  fmtINR, fmtNum, fmtScore, fmtPct, fmtBps, HAZARD_COLORS, HAZARD_LABELS, riskColor,
} from "../utils/format";

export default function Dashboard() {
  const navigate = useNavigate();
  const [scenario, setScenario] = useState("current");
  const [kpis, setKpis] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rescoring, setRescoring] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [k, p] = await Promise.all([
        dashboardApi.kpis(scenario),
        riskApi.cachedPortfolio(scenario),
      ]);
      setKpis(k.data.kpis);
      setPortfolio(p.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scenario]);

  const rescore = async () => {
    setRescoring(true);
    try {
      const { data } = await riskApi.rescorePortfolio(scenario);
      toast.success(`Re-scored ${data.summary.collateral_count} collaterals`);
      await load();
    } catch (e) { toast.error("Re-score failed"); }
    finally { setRescoring(false); }
  };

  const summary = portfolio?.summary || kpis?.portfolio_summary || {};
  const byIndustry = portfolio?.by_industry || [];
  const byPortfolio = portfolio?.by_portfolio || [];
  const hazardBreakdown = summary.hazard_breakdown || [];
  const results = portfolio?.results || [];

  const top5 = useMemo(() => results.slice(0, 6), [results]);

  return (
    <DashboardLayout
      title="Executive Overview"
      subtitle="CLIMATE PHYSICAL RISK · INDIA PORTFOLIO"
      headerExtra={
        <Box sx={{ display: "flex", gap: 1.2, alignItems: "center" }}>
          <ScenarioToggle value={scenario} onChange={setScenario} />
          <Button
            variant="contained" size="small"
            disabled={rescoring}
            onClick={rescore}
            startIcon={rescoring ? <CircularProgress size={12} sx={{ color: "#FFF" }} /> : <RefreshCw size={13} />}
            data-testid="rescore-btn"
            sx={{ background: "#2E5A35", "&:hover": { background: "#1f4023" } }}
          >
            {rescoring ? "Re-scoring" : "Re-score portfolio"}
          </Button>
        </Box>
      }
    >
      {loading ? (
        <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={26} sx={{ color: "#1E2A38" }} /></Box>
      ) : (
        <>
          {/* KPI ROW */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" }, gap: 1.6, mb: 2.2 }}>
            <div className="cb-fade-up cb-fade-up-1">
              <KpiCard
                label="Total Exposure"
                value={fmtINR(summary.total_financial_exposure_inr)}
                sub={`across ${fmtNum(summary.collateral_count)} collateral${summary.collateral_count === 1 ? "" : "s"}`}
                accent="#1E2A38"
                icon={<Banknote size={16} />}
                testid="kpi-exposure"
              />
            </div>
            <div className="cb-fade-up cb-fade-up-2">
              <KpiCard
                label="High-Risk Share"
                value={fmtPct(summary.high_risk_pct)}
                sub={`${(summary.level_counts?.High || 0) + (summary.level_counts?.Critical || 0)} collaterals`}
                accent="#A93226"
                icon={<AlertTriangle size={16} />}
                testid="kpi-high-risk"
              />
            </div>
            <div className="cb-fade-up cb-fade-up-3">
              <KpiCard
                label="Borrowers"
                value={fmtNum(kpis?.n_borrowers)}
                sub={`${fmtNum(kpis?.n_lending_borrowers)} lending · ${fmtNum(kpis?.n_investment_borrowers)} investment`}
                accent="#2E5A35"
                icon={<Database size={16} />}
                testid="kpi-borrowers"
              />
            </div>
            <div className="cb-fade-up cb-fade-up-4">
              <KpiCard
                label="Collateral Value"
                value={fmtINR(kpis?.total_collateral_value_inr)}
                sub={`${fmtNum(kpis?.n_collaterals)} assets pinpointed`}
                accent="#8F5E14"
                icon={<Building2 size={16} />}
                testid="kpi-collateral-value"
              />
            </div>
            <div className="cb-fade-up cb-fade-up-4">
              <KpiCard
                label="Avg PD Uplift"
                value={fmtBps(summary.avg_pd_uplift_bps)}
                sub={`climate-driven · ${scenario}`}
                accent="#2C3E50"
                icon={<TrendingUp size={16} />}
                testid="kpi-pd-uplift"
              />
            </div>
          </Box>

          {/* MAIN GRID */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr" }, gap: 2 }}>
            {/* Portfolio risk distribution + hazard breakdown */}
            <Paper sx={{ p: 2.4 }} data-testid="hazard-breakdown-panel" className="cb-fade-up cb-fade-up-2">
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 2 }}>
                <Box>
                  <Box className="cb-display" sx={{ fontSize: 17, fontWeight: 600, color: "#111827" }}>
                    Hazard Exposure Breakdown
                  </Box>
                  <Box sx={{ fontSize: 12, color: "#6B7280", mt: 0.2 }}>
                    Average score and aggregate exposure per climate hazard.
                  </Box>
                </Box>
                <Button size="small" variant="outlined" onClick={() => navigate("/hazard-map")}
                  endIcon={<ArrowRight size={13} />} data-testid="open-map-btn">
                  Open hazard map
                </Button>
              </Box>

              {hazardBreakdown.length === 0 ? (
                <Box sx={{ py: 4, color: "#94A3B8", fontSize: 13, textAlign: "center" }}>
                  No risk scores yet. Click "Re-score portfolio" to compute.
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                  {hazardBreakdown.map((h) => (
                    <Box key={h.hazard_id} data-testid={`hazard-row-${h.hazard_id}`}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: HAZARD_COLORS[h.hazard_id] || "#6B7280" }} />
                          <Box sx={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                            {HAZARD_LABELS[h.hazard_id] || h.hazard_name}
                          </Box>
                          <Chip size="small" label={`${h.high_risk_collateral_count} hi-risk`} sx={{ fontSize: 9.5, height: 18 }} />
                        </Box>
                        <Box className="cb-mono" sx={{ fontSize: 12, color: "#4B5563" }}>
                          {fmtINR(h.financial_exposure_inr)} · score {fmtScore(h.avg_score)}
                        </Box>
                      </Box>
                      <Box sx={{ background: "#F0EFEA", borderRadius: 1, height: 8, overflow: "hidden" }}>
                        <Box sx={{
                          width: `${Math.min(100, h.avg_score)}%`,
                          height: "100%", background: HAZARD_COLORS[h.hazard_id] || "#6B7280",
                          transition: "width 0.6s ease",
                        }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>

            {/* Right column: risk level distribution + portfolio splits */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Paper sx={{ p: 2.4 }} data-testid="risk-level-distribution" className="cb-fade-up cb-fade-up-3">
                <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827", mb: 1.5 }}>
                  Risk-Level Distribution
                </Box>
                {["Critical", "High", "Medium", "Low"].map((lvl) => {
                  const n = summary.level_counts?.[lvl] || 0;
                  const total = summary.collateral_count || 1;
                  const pct = (n / total) * 100;
                  return (
                    <Box key={lvl} sx={{ mb: 1.2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                          <RiskPill level={lvl} />
                          <Box sx={{ fontSize: 12, color: "#4B5563" }}>{n} of {total}</Box>
                        </Box>
                        <Box className="cb-mono" sx={{ fontSize: 12, color: "#111827", fontWeight: 600 }}>{pct.toFixed(0)}%</Box>
                      </Box>
                      <Box sx={{ background: "#F0EFEA", borderRadius: 1, height: 5, overflow: "hidden" }}>
                        <Box sx={{ width: `${pct}%`, height: "100%", background: riskColor(lvl), transition: "width 0.5s ease" }} />
                      </Box>
                    </Box>
                  );
                })}
              </Paper>

              <Paper sx={{ p: 2.4 }} className="cb-fade-up cb-fade-up-4">
                <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827", mb: 1.5 }}>
                  By Portfolio
                </Box>
                {byPortfolio.length === 0 ? (
                  <Box sx={{ fontSize: 12, color: "#94A3B8" }}>No data.</Box>
                ) : byPortfolio.map((p) => (
                  <Box key={p.portfolio} sx={{ display: "flex", justifyContent: "space-between", py: 0.7, borderTop: "1px solid #F0EFEA" }}>
                    <Box sx={{ fontSize: 13, fontWeight: 600, color: "#111827", textTransform: "capitalize" }}>
                      {p.portfolio}
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Box className="cb-mono" sx={{ fontSize: 12, color: "#4B5563" }}>score {fmtScore(p.avg_score)}</Box>
                      <Box className="cb-mono" sx={{ fontSize: 12, color: "#111827", fontWeight: 600 }}>{fmtINR(p.fin_sum)}</Box>
                    </Box>
                  </Box>
                ))}
              </Paper>
            </Box>
          </Box>

          {/* Top high-risk collaterals */}
          <Paper sx={{ p: 0, mt: 2, overflow: "hidden" }} data-testid="top-collaterals-table" className="cb-fade-up cb-fade-up-4">
            <Box sx={{ p: 2.2, display: "flex", justifyContent: "space-between", alignItems: "baseline", borderBottom: "1px solid #F0EFEA" }}>
              <Box>
                <Box className="cb-display" sx={{ fontSize: 17, fontWeight: 600, color: "#111827" }}>
                  Highest-risk Collaterals
                </Box>
                <Box sx={{ fontSize: 12, color: "#6B7280" }}>Top {top5.length} of {results.length}</Box>
              </Box>
              <Button size="small" variant="outlined" onClick={() => navigate("/collaterals")} endIcon={<ArrowRight size={13} />}>
                View all collateral
              </Button>
            </Box>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Collateral</TableCell>
                  <TableCell>Borrower · Portfolio</TableCell>
                  <TableCell>Top hazard</TableCell>
                  <TableCell align="right">Score</TableCell>
                  <TableCell align="right">PD uplift</TableCell>
                  <TableCell align="right">Exposure</TableCell>
                  <TableCell align="right">Risk</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {top5.map((r) => (
                  <TableRow key={r.collateral_id} hover>
                    <TableCell>
                      <Box sx={{ fontWeight: 600, color: "#111827" }}>{r.collateral_name || r.collateral_id?.slice(0, 8)}</Box>
                      <Box className="cb-mono" sx={{ fontSize: 11, color: "#6B7280" }}>{r.city || ""} · {r.state || ""} · {r.pin_code || ""}</Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ fontWeight: 600, color: "#111827", fontSize: 13 }}>{r.borrower_name}</Box>
                      <Box className="cb-mono" sx={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase" }}>
                        {r.borrower_cif_no} · {r.borrower_portfolio}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <span className="hazard-chip" data-testid={`top-haz-${r.collateral_id}`}>{HAZARD_LABELS[r.top_hazard] || r.top_hazard || "—"}</span>
                    </TableCell>
                    <TableCell align="right" className="cb-mono" sx={{ fontWeight: 600 }}>{fmtScore(r.overall_score)}</TableCell>
                    <TableCell align="right" className="cb-mono">{fmtBps(r.pd_uplift_bps)}</TableCell>
                    <TableCell align="right" className="cb-mono">{fmtINR(r.financial_exposure_inr)}</TableCell>
                    <TableCell align="right"><RiskPill level={r.risk_level} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Open detail">
                        <Button size="small" data-testid={`open-collateral-${r.collateral_id}`}
                          onClick={() => navigate(`/collaterals/${r.collateral_id}`)}
                          sx={{ minWidth: 0, color: "#1E2A38" }}>
                          <ArrowRight size={14} />
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {top5.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 4, color: "#94A3B8", textAlign: "center" }}>
                      No risk scores yet — click "Re-score portfolio".
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {/* By Industry */}
          <Paper sx={{ p: 2.4, mt: 2 }} data-testid="by-industry-panel" className="cb-fade-up cb-fade-up-4">
            <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827", mb: 1.5 }}>
              Risk by Sector
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.4 }}>
              {byIndustry.map((s) => (
                <Box key={s.industry} sx={{ p: 1.6, border: "1px solid #E2E8F0", borderRadius: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <Box sx={{ fontSize: 13, fontWeight: 600, color: "#111827", textTransform: "capitalize" }}>
                      {s.industry?.replace("_", " ")}
                    </Box>
                    <Box className="cb-mono" sx={{ fontSize: 11, color: "#6B7280" }}>{s.count} {s.count === 1 ? "asset" : "assets"}</Box>
                  </Box>
                  <Box className="cb-mono" sx={{ fontSize: 16, fontWeight: 600, color: "#111827", mt: 0.4 }}>
                    {fmtScore(s.avg_score)}
                  </Box>
                  <Box className="cb-mono" sx={{ fontSize: 11.5, color: "#4B5563", mt: 0.2 }}>{fmtINR(s.fin_sum)}</Box>
                </Box>
              ))}
              {byIndustry.length === 0 && <Box sx={{ fontSize: 12, color: "#94A3B8" }}>No sector data yet.</Box>}
            </Box>
          </Paper>
        </>
      )}
    </DashboardLayout>
  );
}
